import { useEffect, useRef, useState } from 'react';

import { ApiError, apiRequest } from '../api/client.js';
import type { PlatformName } from '../api/types.js';

/**
 * Confirm disconnecting a platform (ARCHITECTURE.md §35, §65).
 *
 * ==================== WHY A DIALOG AND NOT A BUTTON ====================
 * Disconnecting destroys the workspace's stored credentials and, for a
 * platform that pushes, tells the provider to stop. It cannot be undone from
 * here — recovering means going back through the provider's consent screen —
 * and the button sits directly beside "Reconnect", which is the safe
 * neighbouring action a misclick would have intended.
 *
 * Built on the native `<dialog>`: focus trapping, Escape to dismiss, the page
 * behind it made inert, and correct semantics for assistive technology, all of
 * which a hand-rolled overlay gets wrong by default.
 *
 * `Cancel` is the autofocused control, not `Disconnect`. A confirmation whose
 * destructive option is the one already focused defeats the purpose: Enter
 * would carry straight through it.
 */

export type DisconnectDialogProps = {
  /** The connection to remove, or null when the dialog is closed. */
  target: { connectionId: string; platform: PlatformName; displayName: string } | null;
  onClose: () => void;
  /** Called after the connection is actually gone, so the list can reload. */
  onDisconnected: () => void | Promise<void>;
};

/**
 * What the operator is told will happen, beyond losing the connection.
 *
 * Only stated where it is true. Instagram is the one platform Connaro can ask
 * to stop pushing; claiming "we told the provider" for Twitch would be
 * describing a call that is never made.
 */
const PROVIDER_NOTES: Partial<Record<PlatformName, string>> = {
  instagram:
    'Connaro will also ask Meta to stop sending Live comments for this account. Removing Connaro’s access to your Instagram account itself is done from Instagram’s own app settings.',
};

export function DisconnectDialog({
  target,
  onClose,
  onDisconnected,
}: DisconnectDialogProps): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * showModal() rather than the `open` attribute. Only the former makes the
   * rest of the page inert and traps focus; setting `open` yields a dialog a
   * keyboard user can tab straight out of and act on the page behind.
   */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    if (target !== null && !dialog.open) {
      setError(null);
      setWorking(false);
      dialog.showModal();
    } else if (target === null && dialog.open) {
      dialog.close();
    }
  }, [target]);

  const confirm = async (): Promise<void> => {
    if (target === null) return;

    setWorking(true);
    setError(null);

    try {
      await apiRequest(`/connections/${target.connectionId}/disconnect`, { method: 'POST' });

      /*
       * Reload BEFORE closing. Closing first would show the old card for as
       * long as the refetch takes, with the platform still reading as
       * connected — a state the operator has every reason to read as failure.
       */
      await onDisconnected();
      onClose();
    } catch (caught) {
      setWorking(false);
      setError(
        caught instanceof ApiError
          ? caught.message
          : `Could not disconnect ${target.displayName}. Please try again.`,
      );
    }
  };

  const note = target === null ? undefined : PROVIDER_NOTES[target.platform];

  return (
    <dialog
      ref={dialogRef}
      className="dialog dialog--narrow"
      aria-labelledby="disconnect-title"
      // Escape fires `cancel`; the close button fires `close`. Reporting both
      // keeps React's state in step with the element's own, which otherwise
      // drift apart the first time someone presses Escape.
      onClose={onClose}
      onCancel={onClose}
    >
      <div className="dialog__header">
        <h2 className="dialog__title" id="disconnect-title">
          Disconnect {target?.displayName ?? ''}?
        </h2>
      </div>

      <div className="dialog__body">
        <p style={{ margin: '0 0 var(--s-3)', fontSize: 'var(--text-sm)' }}>
          Connaro will delete the stored credentials for this workspace and stop reading chat from{' '}
          {target?.displayName ?? 'this platform'}. To use it again you will have to authorize it
          again.
        </p>

        {note === undefined ? null : (
          <p
            style={{
              margin: '0 0 var(--s-3)',
              fontSize: 'var(--text-sm)',
              color: 'var(--c-text-muted)',
            }}
          >
            {note}
          </p>
        )}

        {/*
         * Said plainly, because it is the question an operator actually has and
         * the answer is not what they expect from the word "disconnect".
         */}
        <p
          style={{
            margin: '0 0 var(--s-4)',
            fontSize: 'var(--text-sm)',
            color: 'var(--c-text-muted)',
          }}
        >
          Chat already saved for this workspace is kept, and expires on its normal retention
          schedule.
        </p>

        {error === null ? null : (
          <p role="alert" style={{ color: 'var(--c-danger)', fontSize: 'var(--text-sm)' }}>
            {error}
          </p>
        )}
      </div>

      <div className="dialog__footer">
        {/*
         * Cancel first in the DOM and autofocused, so the destructive action is
         * never the one Enter reaches by default.
         */}
        <button
          type="button"
          className="btn btn--secondary"
          onClick={onClose}
          disabled={working}
          autoFocus
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn--danger"
          onClick={() => {
            void confirm();
          }}
          disabled={working}
        >
          {working ? 'Disconnecting…' : `Disconnect ${target?.displayName ?? ''}`}
        </button>
      </div>
    </dialog>
  );
}
